import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import Register from "./pages/auth/Register";
import Dashboard from "./pages/dashboard/Dashboard";
import Sidebar from "./components/sidebar/SideBar";
import Layout from "./components/layout/Layout";
import axios from "axios";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef } from "react";
import { getLoginStatus } from "./services/authService";
import { SET_LOGIN, SET_NAME, SET_USER, LOGOUT } from "./redux/features/auth/authSlice";
import { toast } from 'react-toastify';
import ProductsTable from "./pages/products/ProductsTable";
import ProductTableAdmin from "./pages/products/ProductTableAdmin";
import CreateOrder from "./pages/orders/CreateOrder";
import OrdersDashBoard from "./pages/orders/OrdersDashBoard";
import CreateProduct from "./pages/products/CreateProduct";
import UsersTable from "./pages/users/UsersTable";
import UpdateUser from "./pages/users/UpdateUser"; 
import UpdateProduct from "./pages/products/UpdateProduct";
import ReturnOrder from "./pages/orders/ReturnOrder";
import UpdateOrder from "./pages/orders/UpdateOrder";
import { PrivateRoute } from "./components/protectedRoute/ProtectedRoute";
import { AdminRoute } from "./components/protectedRoute/ProtectedRoute";
import CategoriesAndWarehouses from './pages/categoriesAndWarehouses/CategoriesAndWarehouses';
import AuthPage from "./pages/auth/AuthPage";
import NotFound from "./pages/notFound/NotFound";
import AlertManagement from './pages/AlertManagement/AlertManagement'; // Adjust path as needed
import FaultyItemsPage from "./components/product/FaultyItemsPage";
import CreateFaultyItem from './pages/faultyItems/CreateFaultyItem';
import EditFaultyItem from "./pages/faultyItems/EditFaultyItem";

import './global.css';


axios.defaults.withCredentials = true;

// Add axios interceptor to handle 401 errors globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't show multiple toasts for the same 401 error
      const isLoginPage = window.location.pathname === '/';
      if (!isLoginPage) {
        console.log('Unauthorized request detected, redirecting to login');
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const lastUserIdRef = useRef(null);
  const hasShownWarningRef = useRef(false);

  useEffect(() =>{
    async function loginStatus() {
      const status = await getLoginStatus();
      // Only update login state if the server says the session is invalid.
      // Don't override a valid Redux session (e.g. user just logged in) with a
      // transient false response from the server.
      if (!status) {
        dispatch(SET_LOGIN(false));
      }
    }
    loginStatus();
  }, [dispatch])

  // Monitor for session changes across tabs
  useEffect(() => {
    const checkSessionChange = async () => {
      const storedUserId = localStorage.getItem('activeUserId');
      const storedIsLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
      
      // If someone logged in on another tab with different user
      if (storedIsLoggedIn && storedUserId && currentUser._id) {
        if (storedUserId !== currentUser._id && !hasShownWarningRef.current) {
          console.log('Session conflict detected: Different user logged in on another tab');
          hasShownWarningRef.current = true;
          
          toast.warning('Another user logged in. Syncing session...', {
            position: "top-center",
            autoClose: 3000,
          });
          
          // Fetch the new user's data from server
          try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/getuser/current-user`);
            if (response.data) {
              dispatch(SET_USER(response.data));
              dispatch(SET_NAME(response.data.name));
              dispatch(SET_LOGIN(true));
              
              // Force page reload to clear any cached data
              setTimeout(() => {
                window.location.reload();
              }, 500);
            }
          } catch (error) {
            console.error('Failed to sync user session:', error);
            // If can't get user data, logout
            dispatch(LOGOUT());
            window.location.href = '/';
          }
        }
      }
      
      // If someone logged out on another tab
      if (!storedIsLoggedIn && isLoggedIn) {
        console.log('User logged out on another tab');
        dispatch(LOGOUT());
        window.location.href = '/';
      }
      
      lastUserIdRef.current = storedUserId;
    };

    // Check every 1 second for session changes
    const intervalId = setInterval(checkSessionChange, 1000);

    // Listen to storage events from other tabs
    const handleStorageChange = (e) => {
      if (e.key === 'activeUserId' || e.key === 'isLoggedIn') {
        checkSessionChange();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [dispatch, currentUser._id, isLoggedIn])
  
  return (
    <BrowserRouter>
      <ToastContainer/>
      <Routes>
        <Route path="/" element={<AuthPage/>}/>
        {/* <Route path="/register" element={<Register/>}/>  */}
        <Route path="/register" element={<AdminRoute><Register/></AdminRoute>}/> 

        <Route path="/dashboard" element={
          <PrivateRoute>
          <Sidebar>
            <Layout>
              <Dashboard/>
            </Layout>
          </Sidebar>
          </PrivateRoute>
        }/>

        <Route path="/getproducts" element={
          <PrivateRoute>
          <Sidebar>
            <Layout>
              <ProductsTable/>
            </Layout>
          </Sidebar>
          </PrivateRoute>
        }/>

          <Route path="/CreateFaultyItem" element={
          <PrivateRoute>
          <Sidebar>
            <Layout>
              <CreateFaultyItem/>
            </Layout>
          </Sidebar>
          </PrivateRoute>
        }/>

        <Route path="/EditFaultyItem" element={
          <PrivateRoute>
          <Sidebar>
            <Layout>
              <EditFaultyItem/>
            </Layout>
          </Sidebar>
          </PrivateRoute>
        }/>

        <Route path="/FaultyItemsPage" element={
          <PrivateRoute>
          <Sidebar>
            <Layout>
              <FaultyItemsPage/>
            </Layout>
          </Sidebar>
          </PrivateRoute>
        }/>

        <Route path="/product-maintenance" element={
          <AdminRoute permission={['admin','editor']}>
          <Sidebar>
            <Layout>
              <ProductTableAdmin/>
            </Layout>
          </Sidebar>
          </AdminRoute>
        }/>

        <Route path="/alert-management" element={
          <AdminRoute permission={['admin',]}>
          <Sidebar>
            <Layout>
              <AlertManagement/>
            </Layout>
          </Sidebar>
          </AdminRoute>
        }/>

        <Route path="/user-maintenance" element={
          <AdminRoute>
          <Sidebar>
            <Layout>
              <UsersTable/>
            </Layout>
          </Sidebar>
          </AdminRoute>
        }/>
        <Route path="/update-user" element={
          <AdminRoute>
          <Sidebar>
            <Layout>
              <UpdateUser/>
            </Layout>
          </Sidebar>
          </AdminRoute>
        }/>
        <Route path="/categories-warehouses" element={
          <AdminRoute permission={['admin','editor']}>
            <Sidebar>
              <Layout>
                <CategoriesAndWarehouses />
              </Layout>
            </Sidebar>
          </AdminRoute>
        }/>
        <Route path="/categories-warehouses" element={
          <AdminRoute permission={['admin','editor']}>
            <Sidebar>
              <Layout>
                <CategoriesAndWarehouses />
              </Layout>
            </Sidebar>
          </AdminRoute>
        }/>

        <Route path="/createproduct" element={
          <AdminRoute permission={['admin','editor']}>
          <Sidebar>
            <Layout>
              <CreateProduct/>
            </Layout>
          </Sidebar>
          </AdminRoute>
        }/>

        <Route path="/update-product" element={
          <AdminRoute permission={['admin','editor']}>
          <Sidebar>
            <Layout>
              <UpdateProduct/>
            </Layout>
          </Sidebar>
          </AdminRoute >
        }/>
        <Route path="/create-order" element={
          <PrivateRoute>
          <Sidebar>
            <Layout>
              <CreateOrder/>
            </Layout>
          </Sidebar>
          </PrivateRoute>
        }/>
        <Route path="/orders" element={
          <PrivateRoute>
          <Sidebar>
            <Layout>
              <OrdersDashBoard/>
            </Layout>
          </Sidebar>
          </PrivateRoute>
        }/>
        <Route path="/return-order" element={
          <PrivateRoute>
          <Sidebar>
            <Layout>
              <ReturnOrder/>
            </Layout>
          </Sidebar>
          </PrivateRoute>
        }/>
        <Route path="/update-order" element={
          <PrivateRoute>
          <Sidebar>
            <Layout>
              <UpdateOrder/>
            </Layout>
          </Sidebar>
          </PrivateRoute>
        }/>
        <Route path="*" element={<NotFound/>}/>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
